import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { 
  Calculator, Euro, TrendingUp, AlertTriangle, 
  CheckCircle2, Save, RotateCcw, Building2, Download, Layers, ExternalLink, Kanban,
  Eye, Trash2, Rocket
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { downloadBlob } from '../utils/download';
import { ConfirmButton } from '../components/ConfirmButton';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const InputField = ({ label, value, onChange, suffix, type = 'number', tooltip, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-zinc-400 text-sm">{label}</Label>
    <div className="relative">
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-950 border-zinc-800 text-white pr-12"
        {...props}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">{suffix}</span>
      )}
    </div>
  </div>
);

const ResultRow = ({ label, value, isProfit, isBold, isMono }) => (
  <div className={`flex justify-between items-center py-2 ${isBold ? 'font-semibold' : ''}`}>
    <span className="text-zinc-400">{label}</span>
    <span className={`${isMono ? 'font-mono' : ''} ${
      isProfit === true ? 'text-emerald-500' : 
      isProfit === false ? 'text-red-500' : 
      'text-white'
    }`}>
      {formatCurrency(value)}
    </span>
  </div>
);

export const StudyPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState(null);
  
  const [formData, setFormData] = useState({
    prix_achat: 200000,
    frais_notaire_pct: 2.0,
    frais_agence: 0,
    travaux_estimation: 30000,
    travaux_imprevus_pct: 10,
    honoraires_archi_pct: 0,
    assurance_dommages_ouvrage: 0,
    frais_geometre: 0,
    diagnostics_obligatoires: 800,
    frais_dossier_banque: 0,
    frais_courtage: 0,
    frais_garantie: 0,
    assurance_emprunteur_mensuelle: 0,
    financement_mode: 'emprunt',  // 'emprunt' | 'fonds_propres'
    apport: 0,                    // apport personnel — réduit le capital emprunté
    travaux_finances_par_emprunt: true,  // si false, travaux en fonds propres (pas d'intérêts dessus)
    duree_portage_mois: 12,
    taux_emprunt_annuel: 4.5,
    assurance_pno: 500,
    taxe_fonciere: 1200,
    charges_copro: 0,
    prix_revente_estime: 280000,
    frais_agence_revente_pct: 5,
    home_staging: 0,
    frais_publicite: 0,
    tva_sur_marge: false,
    regime_tva: 'marge',      // 'marge' | 'total' | 'exoneree'
    taux_tva_total: 20,
    taux_is: 15,
    duree_travaux_semaines: '',
    date_revente_estimee: '',
    nom: '',
  });
  const [savedStudies, setSavedStudies] = useState([]);

  // Liste triée des études (anti-recompute à chaque render)
  const sortedSavedStudies = useMemo(() => {
    return [...savedStudies]
      .sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''))
      .slice(0, 8);
  }, [savedStudies]);

  const fetchProperties = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(res.data);
    } catch (err) { console.error('Error fetching properties:', err); }
  }, []);

  const fetchSavedStudies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/studies`, { withCredentials: true });
      setSavedStudies(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchProperties(); fetchSavedStudies(); }, [fetchProperties, fetchSavedStudies]);

  // Handle cross-navigation query params : ?property_id=... or ?study_id=...
  useEffect(() => {
    const propId = searchParams.get('property_id');
    const studyId = searchParams.get('study_id');
    if (studyId) {
      loadStudy(studyId);
      // Remove the param to avoid reloading on every render
      searchParams.delete('study_id');
      setSearchParams(searchParams, { replace: true });
    } else if (propId && properties.length > 0) {
      handlePropertyChange(propId);
      searchParams.delete('property_id');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, searchParams]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateStudy = useCallback(async () => {
    if (!selectedProperty) {
      toast.error('Sélectionnez un bien');
      return;
    }
    setLoading(true);
    try {
      // Coerce ALL numeric form fields to float/int to avoid silent NaN on backend
      const numericFields = [
        'prix_achat', 'frais_notaire_pct', 'frais_agence', 'travaux_estimation',
        'travaux_imprevus_pct', 'honoraires_archi_pct', 'assurance_dommages_ouvrage',
        'frais_geometre', 'diagnostics_obligatoires', 'frais_dossier_banque',
        'frais_courtage', 'frais_garantie', 'assurance_emprunteur_mensuelle', 'apport',
        'taux_emprunt_annuel', 'assurance_pno', 'taxe_fonciere', 'charges_copro',
        'prix_revente_estime', 'frais_agence_revente_pct', 'home_staging',
        'frais_publicite', 'taux_is', 'taux_tva_total',
      ];
      const intFields = ['duree_portage_mois'];
      const coerced = { ...formData };
      numericFields.forEach(f => { coerced[f] = parseFloat(coerced[f]) || 0; });
      intFields.forEach(f => { coerced[f] = parseInt(coerced[f]) || 0; });
      // String-typed fields kept as-is
      coerced.financement_mode = formData.financement_mode || 'emprunt';
      coerced.travaux_finances_par_emprunt = formData.travaux_finances_par_emprunt !== false;
      coerced.regime_tva = formData.regime_tva || 'marge';
      const data = { property_id: selectedProperty, ...coerced };
      if (!navigator.onLine) {
        // OFFLINE : calculer en local avec les company settings cachés
        const cachedSettings = (await import('../offline/db')).getMeta('company_settings_cache')
          .then((c) => c || {})
          .catch(() => ({}));
        const settings = await cachedSettings;
        const result = (await import('../offline/repo')).calculateStudyLocal(data, settings);
        setCalculation(result);
      } else {
        const res = await axios.post(`${API}/studies/calculate`, data, { withCredentials: true });
        setCalculation(res.data);
        // Cache settings each time for offline use
        try {
          const cs = await axios.get(`${API}/company-settings`, { withCredentials: true });
          (await import('../offline/db')).setMeta('company_settings_cache', cs.data);
        } catch { /* ignore */ }
      }
    } catch (err) {
      // Fallback : calculer en local si l'API échoue
      try {
        const result = (await import('../offline/repo')).calculateStudyLocal(
          { property_id: selectedProperty, ...formData }, {});
        setCalculation(result);
      } catch {
        toast.error('Erreur de calcul');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, formData]);

  useEffect(() => {
    if (selectedProperty) {
      const timer = setTimeout(() => {
        calculateStudy();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData, selectedProperty, calculateStudy]);

  const handleSave = async () => {
    if (!calculation || !selectedProperty) return;
    try {
      const payload = buildStudyPayload();
      const offlineRepo = await import('../offline/repo');
      if (savedStudyId) {
        await offlineRepo.updateStudy(savedStudyId, payload);
        toast.success(navigator.onLine ? 'Étude mise à jour' : 'Étude mise à jour (hors-ligne, sera synchronisée)');
      } else {
        const res = await offlineRepo.createStudy(payload);
        if (navigator.onLine) {
          if (res?.project_created) toast.success('Étude enregistrée + Projet créé dans le Pipeline (Prospection)');
          else toast.success('Étude enregistrée');
        } else {
          toast.success('Étude enregistrée (hors-ligne, sera synchronisée)');
        }
        setSavedStudyId(res.id);
      }
      fetchSavedStudies();
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const loadStudy = async (studyId) => {
    if (!studyId) return;
    try {
      const res = await axios.get(`${API}/studies/${studyId}`, { withCredentials: true });
      const s = res.data;
      setSelectedProperty(s.property_id);
      setSavedStudyId(s.id);
      setFormData({
        prix_achat: s.prix_achat || 0,
        frais_notaire_pct: s.frais_notaire_pct ?? 2.0,
        frais_agence: s.frais_agence || 0,
        travaux_estimation: s.travaux_estimation || 0,
        travaux_imprevus_pct: s.travaux_imprevus_pct ?? 10,
        honoraires_archi_pct: s.honoraires_archi_pct || 0,
        assurance_dommages_ouvrage: s.assurance_dommages_ouvrage || 0,
        frais_geometre: s.frais_geometre || 0,
        diagnostics_obligatoires: s.diagnostics_obligatoires || 0,
        frais_dossier_banque: s.frais_dossier_banque || 0,
        frais_courtage: s.frais_courtage || 0,
        frais_garantie: s.frais_garantie || 0,
        assurance_emprunteur_mensuelle: s.assurance_emprunteur_mensuelle || 0,
        duree_portage_mois: s.duree_portage_mois ?? 12,
        taux_emprunt_annuel: s.taux_emprunt_annuel ?? 4.5,
        financement_mode: s.financement_mode || 'emprunt',
        apport: s.apport || 0,
        travaux_finances_par_emprunt: s.travaux_finances_par_emprunt !== false,
        assurance_pno: s.assurance_pno || 0,
        taxe_fonciere: s.taxe_fonciere || 0,
        charges_copro: s.charges_copro || 0,
        prix_revente_estime: s.prix_revente_estime || 0,
        frais_agence_revente_pct: s.frais_agence_revente_pct ?? 5,
        home_staging: s.home_staging || 0,
        frais_publicite: s.frais_publicite || 0,
        tva_sur_marge: !!s.tva_sur_marge,
        regime_tva: s.regime_tva || (s.tva_sur_marge ? 'marge' : 'exoneree'),
        taux_tva_total: s.taux_tva_total ?? 20,
        taux_is: s.taux_is ?? 15,
        duree_travaux_semaines: s.duree_travaux_semaines ?? '',
        date_revente_estimee: s.date_revente_estimee || '',
        nom: s.nom || '',
      });
      toast.success('Étude chargée');
    } catch { toast.error('Impossible de charger l\'étude'); }
  };

  const handleNewStudy = () => {
    setSavedStudyId(null);
    setCalculation(null);
    setFormData(prev => ({
      ...prev,
      nom: '',
      duree_travaux_semaines: '',
      date_revente_estimee: '',
    }));
  };

  const handleDeleteStudy = async (studyId) => {
    try {
      const res = await axios.delete(`${API}/studies/${studyId}`, { withCredentials: true });
      const c = res.data?.cascade || {};
      const parts = [];
      if (c.projects_detached) parts.push(`${c.projects_detached} projet(s) détaché(s)`);
      if (c.appointments_deleted) parts.push(`${c.appointments_deleted} RDV auto supprimé(s)`);
      toast.success(parts.length > 0 ? `Étude supprimée · ${parts.join(', ')}` : 'Étude supprimée');
      if (savedStudyId === studyId) handleNewStudy();
      fetchSavedStudies();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const buildStudyPayload = () => ({
    property_id: selectedProperty,
    ...formData,
    prix_achat: parseFloat(formData.prix_achat),
    frais_notaire_pct: parseFloat(formData.frais_notaire_pct),
    frais_agence: parseFloat(formData.frais_agence),
    travaux_estimation: parseFloat(formData.travaux_estimation),
    travaux_imprevus_pct: parseFloat(formData.travaux_imprevus_pct),
    honoraires_archi_pct: parseFloat(formData.honoraires_archi_pct || 0),
    assurance_dommages_ouvrage: parseFloat(formData.assurance_dommages_ouvrage || 0),
    frais_geometre: parseFloat(formData.frais_geometre || 0),
    diagnostics_obligatoires: parseFloat(formData.diagnostics_obligatoires || 0),
    frais_dossier_banque: parseFloat(formData.frais_dossier_banque || 0),
    frais_courtage: parseFloat(formData.frais_courtage || 0),
    frais_garantie: parseFloat(formData.frais_garantie || 0),
    assurance_emprunteur_mensuelle: parseFloat(formData.assurance_emprunteur_mensuelle || 0),
    apport: parseFloat(formData.apport || 0),
    travaux_finances_par_emprunt: formData.travaux_finances_par_emprunt !== false,
    duree_portage_mois: parseInt(formData.duree_portage_mois),
    taux_emprunt_annuel: parseFloat(formData.taux_emprunt_annuel),
    assurance_pno: parseFloat(formData.assurance_pno),
    taxe_fonciere: parseFloat(formData.taxe_fonciere),
    charges_copro: parseFloat(formData.charges_copro),
    prix_revente_estime: parseFloat(formData.prix_revente_estime),
    frais_agence_revente_pct: parseFloat(formData.frais_agence_revente_pct),
    home_staging: parseFloat(formData.home_staging || 0),
    frais_publicite: parseFloat(formData.frais_publicite || 0),
    taux_is: parseFloat(formData.taux_is),
    duree_travaux_semaines: formData.duree_travaux_semaines ? parseInt(formData.duree_travaux_semaines) : null,
    date_revente_estimee: formData.date_revente_estimee || null,
    nom: formData.nom || null,
  });

  const [savedStudyId, setSavedStudyId] = useState(null);

  const handleExportPdf = async () => {
    if (!selectedProperty) return;
    try {
      const res = await axios.post(`${API}/studies`, buildStudyPayload(), { withCredentials: true });
      const studyId = res.data.id;
      const pdfRes = await axios.get(`${API}/studies/${studyId}/pdf`, { withCredentials: true, responseType: 'blob' });
      downloadBlob(pdfRes.data, `etude_rentabilite_${studyId.slice(0, 8)}.pdf`, 'application/pdf');
      toast.success('PDF téléchargé');
    } catch {
      toast.error('Erreur export PDF');
    }
  };

  const handleLaunchProject = async () => {
    if (!selectedProperty || !calculation) {
      toast.error('Calculez et sélectionnez un bien avant de lancer le projet');
      return;
    }
    try {
      // 1. Save study first (create or update) to persist the latest figures
      let sid = savedStudyId;
      if (sid) {
        await axios.put(`${API}/studies/${sid}`, buildStudyPayload(), { withCredentials: true });
      } else {
        const r = await axios.post(`${API}/studies`, buildStudyPayload(), { withCredentials: true });
        sid = r.data.id;
        setSavedStudyId(sid);
      }
      // 2. Launch → create/promote project with budgets pre-filled
      const res = await axios.post(`${API}/studies/${sid}/launch-project`, {}, { withCredentials: true });
      if (res.data.created) {
        toast.success('🚀 Projet lancé dans le pipeline (Offre en cours) avec budgets pré-remplis');
      } else {
        toast.success('🔄 Projet mis à jour avec les dernières prévisions');
      }
      fetchSavedStudies();
      // 3. Navigate to the pipeline to show the new project
      setTimeout(() => navigate(`/app/pipeline?project_id=${res.data.project_id}`), 600);
    } catch (err) {
      console.error(err);
      toast.error('Impossible de lancer le projet');
    }
  };

  const handlePropertyChange = (propertyId) => {
    setSelectedProperty(propertyId);
    const property = properties.find(p => p.id === propertyId);
    if (property) {
      setFormData(prev => ({
        ...prev,
        prix_achat: property.price || prev.prix_achat,
      }));
    }
  };

  const costBreakdown = calculation ? [
    { name: 'Prix d\'achat', value: calculation.prix_achat, color: '#3B82F6' },
    { name: 'Frais notaire', value: calculation.frais_notaire, color: '#8B5CF6' },
    { name: 'Travaux', value: calculation.cout_travaux_total, color: '#F59E0B' },
    { name: 'Frais portage', value: calculation.cout_portage, color: '#EF4444' },
    { name: 'Frais bancaires', value: calculation.frais_bancaires || 0, color: '#F97316' },
  ].filter(item => item.value > 0) : [];

  const isViable = calculation && calculation.plus_value_nette > 0 && calculation.roi_net_pct >= 10;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="study-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Étude de rentabilité</h1>
          <p className="text-zinc-400 mt-1">Calculez la viabilité de vos projets (Régime IS)</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {savedStudies.length > 0 && (
            <Select value={savedStudyId || 'new'} onValueChange={(v) => v === 'new' ? handleNewStudy() : loadStudy(v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white w-72" data-testid="load-study-select">
                <SelectValue placeholder="Charger une étude" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">+ Nouvelle étude</SelectItem>
                {savedStudies.map(s => {
                  const prop = properties.find(p => p.id === s.property_id);
                  return (
                    <SelectItem key={s.id} value={s.id}>
                      {s.nom || prop?.title || 'Étude'} {prop?.city ? `- ${prop.city}` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
          {savedStudyId && (
            <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300" onClick={handleNewStudy}>
              Nouvelle
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Études récentes (list view with actions) */}
          {savedStudies.length > 0 && (
            <Card className="bg-zinc-900 border-zinc-800" data-testid="etudes-recentes-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Layers className="w-4 h-4 text-emerald-500" />
                  Études récentes
                  <span className="text-xs text-zinc-500 font-normal">({savedStudies.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
                {sortedSavedStudies
                  .map(s => {
                    const prop = properties.find(p => p.id === s.property_id);
                    const pv = s.plus_value_nette ?? 0;
                    const pvColor = pv >= 0 ? 'text-emerald-400' : 'text-red-400';
                    const date = s.updated_at || s.created_at;
                    const dateFmt = date ? new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
                    const isActive = s.id === savedStudyId;
                    return (
                      <div key={s.id}
                        className={`flex items-center gap-2 p-2 rounded-md border ${isActive ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-zinc-800 hover:bg-zinc-800/30'} transition-colors`}
                        data-testid={`etude-row-${s.id}`}
                      >
                        <Building2 className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-white truncate">{s.nom || prop?.title || 'Étude sans nom'}</span>
                            {prop?.city && <span className="text-xs text-zinc-500">· {prop.city}</span>}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                            <span>{dateFmt}</span>
                            <span className={`font-mono ${pvColor}`}>PV nette : {pv >= 0 ? '+' : ''}{pv.toLocaleString('fr-FR')} €</span>
                            {s.roi_net_pct != null && <span className="text-blue-400 font-mono">ROI {s.roi_net_pct.toFixed(1)}%</span>}
                          </div>
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500 hover:text-emerald-400"
                          onClick={() => loadStudy(s.id)} title="Voir / Modifier" data-testid={`etude-view-${s.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <ConfirmButton
                          title="Supprimer cette étude ?"
                          description={`« ${s.nom || prop?.title || 'Étude sans nom'} » sera définitivement supprimée.`}
                          confirmLabel="Supprimer"
                          onConfirm={() => handleDeleteStudy(s.id)}
                          trigger={
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500 hover:text-red-400"
                              title="Supprimer" data-testid={`etude-delete-${s.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          }
                        />
                      </div>
                    );
                  })}
              </CardContent>
            </Card>
          )}

          {/* Property Selection */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
                Sélection du bien & nom de l'étude
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={selectedProperty} onValueChange={handlePropertyChange}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="property-select">
                  <SelectValue placeholder="Sélectionner un bien" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.title} - {prop.city} ({formatCurrency(prop.price)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProperty && (
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/app/recherche?property_id=${selectedProperty}`)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 text-[11px] text-zinc-400 hover:text-white transition-colors"
                    data-testid="nav-to-property"
                  >
                    <ExternalLink className="w-3 h-3" /> Voir la fiche du bien
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/app/pipeline?property_id=${selectedProperty}`)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-purple-500/20 hover:text-purple-400 text-[11px] text-zinc-400 transition-colors"
                    data-testid="nav-to-pipeline"
                  >
                    <Kanban className="w-3 h-3" /> Voir/créer le projet
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/app/visites?property_id=${selectedProperty}`)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-blue-500/20 hover:text-blue-400 text-[11px] text-zinc-400 transition-colors"
                    data-testid="nav-to-visit"
                  >
                    <ExternalLink className="w-3 h-3" /> Visite technique
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <InputField type="text" label="Nom de l'étude (optionnel)" value={formData.nom} onChange={(v) => updateField('nom', v)} data-testid="study-name-input" />
                <InputField label="Durée prévisionnelle travaux" value={formData.duree_travaux_semaines} onChange={(v) => updateField('duree_travaux_semaines', v)} suffix="sem" data-testid="study-duree-travaux" />
                <InputField type="text" label="Date de revente estimée" value={formData.date_revente_estimee} onChange={(v) => updateField('date_revente_estimee', v)} placeholder="YYYY-MM-DD ou YYYY-W42" data-testid="study-date-revente" />
              </div>
            </CardContent>
          </Card>

          {/* Acquisition */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Coûts d'acquisition</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Prix d'achat"
                value={formData.prix_achat}
                onChange={(v) => updateField('prix_achat', v)}
                suffix="€"
                data-testid="prix-achat-input"
              />
              <InputField
                label="Frais de notaire"
                value={formData.frais_notaire_pct}
                onChange={(v) => updateField('frais_notaire_pct', v)}
                suffix="%"
              />
              <InputField
                label="Frais d'agence achat"
                value={formData.frais_agence}
                onChange={(v) => updateField('frais_agence', v)}
                suffix="€"
              />
            </CardContent>
          </Card>

          {/* Travaux */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Travaux & Frais annexes</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Estimation travaux" value={formData.travaux_estimation} onChange={(v) => updateField('travaux_estimation', v)} suffix="EUR" />
              <InputField label="Marge imprevus" value={formData.travaux_imprevus_pct} onChange={(v) => updateField('travaux_imprevus_pct', v)} suffix="%" />
              <InputField label="Honoraires architecte" value={formData.honoraires_archi_pct} onChange={(v) => updateField('honoraires_archi_pct', v)} suffix="% travaux" />
              <InputField label="Assurance dommages-ouvrage" value={formData.assurance_dommages_ouvrage} onChange={(v) => updateField('assurance_dommages_ouvrage', v)} suffix="EUR" />
              <InputField label="Frais geometre" value={formData.frais_geometre} onChange={(v) => updateField('frais_geometre', v)} suffix="EUR" />
              <InputField label="Diagnostics obligatoires" value={formData.diagnostics_obligatoires} onChange={(v) => updateField('diagnostics_obligatoires', v)} suffix="EUR" />
            </CardContent>
          </Card>

          {/* Frais bancaires */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Frais bancaires & Financement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Toggle financement mode */}
              <div className="p-3 bg-zinc-950/50 border border-zinc-800 rounded-lg">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-zinc-300 text-sm font-semibold">Mode de financement</Label>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {formData.financement_mode === 'fonds_propres'
                        ? '💰 Fonds propres : aucun intérêt, aucun frais bancaire, pas d\'assurance emprunteur'
                        : '🏦 Emprunt : intérêts et frais bancaires pris en compte'}
                    </p>
                  </div>
                  <Select value={formData.financement_mode || 'emprunt'} onValueChange={(v) => updateField('financement_mode', v)}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white w-44" data-testid="financement-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emprunt">🏦 Emprunt bancaire</SelectItem>
                      <SelectItem value="fonds_propres">💰 Fonds propres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {formData.financement_mode !== 'fonds_propres' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <InputField label="Frais dossier banque" value={formData.frais_dossier_banque} onChange={(v) => updateField('frais_dossier_banque', v)} suffix="EUR" />
                    <InputField label="Frais de courtage" value={formData.frais_courtage} onChange={(v) => updateField('frais_courtage', v)} suffix="EUR" />
                    <InputField label="Frais de garantie" value={formData.frais_garantie} onChange={(v) => updateField('frais_garantie', v)} suffix="EUR" />
                    <InputField label="Assurance emprunteur /mois" value={formData.assurance_emprunteur_mensuelle} onChange={(v) => updateField('assurance_emprunteur_mensuelle', v)} suffix="EUR" />
                  </div>

                  {/* Apport personnel + toggle travaux dans l'emprunt */}
                  <div className="mt-4 p-3 bg-zinc-950/50 border border-amber-500/20 rounded-lg space-y-3" data-testid="apport-section">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                      <InputField
                        label="Apport personnel"
                        value={formData.apport}
                        onChange={(v) => updateField('apport', v)}
                        suffix="EUR"
                        data-testid="apport-input"
                      />
                      <div className="flex items-start justify-between gap-3 p-2">
                        <div>
                          <Label className="text-zinc-300 text-sm font-semibold">Travaux financés par l'emprunt</Label>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            {formData.travaux_finances_par_emprunt
                              ? '🏦 Inclus dans le capital → génèrent des intérêts'
                              : '💰 Fonds propres → sortis du compte au démarrage, sans intérêts'}
                          </p>
                        </div>
                        <Switch
                          checked={formData.travaux_finances_par_emprunt !== false}
                          onCheckedChange={(v) => updateField('travaux_finances_par_emprunt', v)}
                          data-testid="travaux-emprunt-toggle"
                        />
                      </div>
                    </div>
                    {calculation?.capital_emprunte !== undefined && (
                      <div className="text-xs text-amber-400/90 pt-2 border-t border-zinc-800 flex flex-wrap gap-x-4 gap-y-1" data-testid="capital-emprunte-display">
                        <span>💼 Capital emprunté : <strong className="text-amber-300">{Math.round(calculation.capital_emprunte).toLocaleString('fr-FR')} €</strong></span>
                        <span>📈 Intérêts totaux : <strong className="text-amber-300">{Math.round(calculation.interets_emprunt || 0).toLocaleString('fr-FR')} €</strong></span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Portage */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Frais de portage</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Durée portage"
                value={formData.duree_portage_mois}
                onChange={(v) => updateField('duree_portage_mois', v)}
                suffix="mois"
              />
              {formData.financement_mode !== 'fonds_propres' && (
                <InputField
                  label="Taux emprunt annuel"
                  value={formData.taux_emprunt_annuel}
                  onChange={(v) => updateField('taux_emprunt_annuel', v)}
                  suffix="%"
                />
              )}
              <InputField
                label="Assurance PNO"
                value={formData.assurance_pno}
                onChange={(v) => updateField('assurance_pno', v)}
                suffix="€"
              />
              <InputField
                label="Taxe foncière"
                value={formData.taxe_fonciere}
                onChange={(v) => updateField('taxe_fonciere', v)}
                suffix="€"
              />
              <InputField
                label="Charges copro"
                value={formData.charges_copro}
                onChange={(v) => updateField('charges_copro', v)}
                suffix="€"
              />
            </CardContent>
          </Card>

          {/* Revente */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Revente</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(() => {
                // Suggestion DVF : si une analyse de localisation a été faite sur le bien
                // sélectionné, on propose le prix marché × surface comme prix de revente cible.
                const prop = properties.find((p) => p.id === selectedProperty);
                const dvf = prop?.location_analysis?.dvf;
                const surface = Number(prop?.surface) || 0;
                const avg = Number(dvf?.avg_price_m2) || 0;
                const suggested = avg && surface ? Math.round(avg * surface) : 0;
                const current = Number(formData.prix_revente_estime) || 0;
                if (!suggested) return null;
                const diff = current ? ((current / suggested - 1) * 100) : 0;
                return (
                  <div className="md:col-span-3 bg-emerald-950/30 border border-emerald-700/40 rounded-lg p-3 flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-600/20 flex items-center justify-center">
                      <span className="text-base">📊</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-emerald-300 font-semibold">Suggestion marché DVF</p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        Prix m² moyen sur la zone (rayon {dvf.radius_m || 500}m, {dvf.count_for_avg} ventes fiables) :
                        <span className="text-amber-300 font-mono ml-1">{avg.toLocaleString('fr-FR')} €/m²</span>
                        {' × '}
                        <span className="text-zinc-300">{surface} m²</span>
                        {' = '}
                        <span className="text-emerald-300 font-mono font-bold">{suggested.toLocaleString('fr-FR')} €</span>
                      </p>
                      {current > 0 && (
                        <p className="text-[11px] text-zinc-500 mt-1">
                          Votre cible actuelle : <span className="text-white">{current.toLocaleString('fr-FR')} €</span>
                          {' '}
                          <span className={diff > 5 ? 'text-orange-400' : diff < -5 ? 'text-blue-400' : 'text-emerald-400'}>
                            ({diff >= 0 ? '+' : ''}{diff.toFixed(1)}% vs marché)
                          </span>
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => updateField('prix_revente_estime', suggested)}
                      className="flex-shrink-0 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded transition-colors"
                      data-testid="apply-dvf-suggestion"
                    >
                      Appliquer
                    </button>
                  </div>
                );
              })()}
              <InputField label="Prix de revente estime" value={formData.prix_revente_estime} onChange={(v) => updateField('prix_revente_estime', v)} suffix="EUR" data-testid="prix-revente-input" />
              <InputField label="Frais agence revente" value={formData.frais_agence_revente_pct} onChange={(v) => updateField('frais_agence_revente_pct', v)} suffix="%" />
              <InputField label="Home staging" value={formData.home_staging} onChange={(v) => updateField('home_staging', v)} suffix="EUR" />
              <InputField label="Frais publicite / photos" value={formData.frais_publicite} onChange={(v) => updateField('frais_publicite', v)} suffix="EUR" />
              <div className="md:col-span-2 space-y-2 p-3 bg-zinc-950/50 border border-zinc-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300 text-sm font-semibold">Régime TVA applicable</Label>
                  <a href="https://bofip.impots.gouv.fr" target="_blank" rel="noreferrer" className="text-[10px] text-blue-400 hover:underline">BOFiP ↗</a>
                </div>
                <Select value={formData.regime_tva || 'marge'} onValueChange={(v) => updateField('regime_tva', v)}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="regime-tva-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marge">TVA sur marge (achat particulier, bien &gt; 5 ans)</SelectItem>
                    <SelectItem value="total">TVA sur prix total (vendeur assujetti / bien neuf)</SelectItem>
                    <SelectItem value="exoneree">Exonérée (art. 261 CGI)</SelectItem>
                  </SelectContent>
                </Select>
                {formData.regime_tva === 'total' && (
                  <InputField label="Taux TVA sur total" value={formData.taux_tva_total} onChange={(v) => updateField('taux_tva_total', v)} suffix="%" />
                )}
              </div>
              <InputField label="Taux IS (15% ou 25%)" value={formData.taux_is} onChange={(v) => updateField('taux_is', v)} suffix="%" tooltip="Barème PME : 15% jusqu'à 42 500€ de bénéfice, 25% au-delà. Saisir 25 pour forcer taux normal." />
            </CardContent>
          </Card>
        </div>

        {/* Right: Results */}
        <div className="space-y-6">
          <Card className="bg-zinc-900 border-zinc-800 sticky top-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calculator className="w-5 h-5 text-emerald-500" />
                Résultats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {calculation ? (
                <>
                  {/* Viability Badge */}
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${
                    isViable ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
                  }`}>
                    {isViable ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    )}
                    <span className={isViable ? 'text-emerald-400' : 'text-red-400'}>
                      {isViable ? 'Projet viable' : 'Projet risqué'}
                    </span>
                  </div>

                  <Separator className="bg-zinc-800" />

                  {/* Cost Breakdown */}
                  <div>
                    <h4 className="text-sm font-medium text-zinc-400 mb-3">Répartition des coûts</h4>
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={costBreakdown}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={50}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {costBreakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {costBreakdown.map((item) => (
                        <div key={item.name} className="flex items-center gap-1.5 text-xs">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-zinc-500">{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator className="bg-zinc-800" />

                  {/* Numbers */}
                  <ResultRow label="Cout total acquisition" value={calculation.cout_total_acquisition} isBold isMono />
                  {calculation.frais_bancaires > 0 && <ResultRow label="  dont frais bancaires" value={calculation.frais_bancaires} isMono />}
                  {calculation.frais_commercialisation > 0 && <ResultRow label="  dont commercialisation" value={calculation.frais_commercialisation} isMono />}
                  {calculation.quote_part_structure > 0 && (
                    <ResultRow
                      label="  dont quote-part frais société"
                      value={calculation.quote_part_structure}
                      isMono
                    />
                  )}
                  <ResultRow label="Prix net revente" value={calculation.prix_net_revente} isMono />
                  
                  <Separator className="bg-zinc-800" />
                  
                  <ResultRow label="Plus-value brute" value={calculation.plus_value_brute} isProfit={calculation.plus_value_brute > 0} isMono />
                  {calculation.tva_montant > 0 && (
                    <ResultRow
                      label={
                        calculation.regime_tva === 'total'
                          ? `TVA sur prix total (${formData.taux_tva_total || 20}%)`
                          : 'TVA sur marge (20%)'
                      }
                      value={-calculation.tva_montant}
                      isProfit={false}
                      isMono
                    />
                  )}
                  {calculation.base_imposable_is !== undefined && calculation.base_imposable_is > 42500 && (
                    <div className="text-[10px] text-amber-400 -mt-1 pl-2 pb-1">
                      ⚠ Base IS &gt; 42 500€ : taux progressif 15% puis 25% appliqué
                    </div>
                  )}
                  <ResultRow label="Impot sur les societes" value={-calculation.is_montant} isProfit={false} isMono />
                  
                  <Separator className="bg-zinc-800" />
                  
                  <ResultRow label="Plus-value nette" value={calculation.plus_value_nette} isProfit={calculation.plus_value_nette > 0} isBold isMono />

                  {/* ROI */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-3 bg-zinc-800 rounded-lg text-center">
                      <p className="text-xs text-zinc-500 uppercase tracking-wide">ROI Brut</p>
                      <p className={`text-xl font-bold font-mono ${calculation.roi_brut_pct > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {calculation.roi_brut_pct.toFixed(1)}%
                      </p>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg text-center">
                      <p className="text-xs text-zinc-500 uppercase tracking-wide">ROI Net</p>
                      <p className={`text-xl font-bold font-mono ${calculation.roi_net_pct >= 10 ? 'text-emerald-500' : calculation.roi_net_pct > 0 ? 'text-amber-500' : 'text-red-500'}`}>
                        {calculation.roi_net_pct.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  {/* Cash-flow mensuel — version épurée */}
                  <Separator className="bg-zinc-800 mt-4" />
                  <div className="space-y-2 mt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Charges de portage
                        <span className="text-[10px] text-zinc-600 ml-1">intérêts, PNO, TF, copro</span>
                      </span>
                      <span className="font-mono text-amber-400">{formatCurrency(calculation.charges_mensuelles)}/mois</span>
                    </div>
                    {calculation.charges_societe_mensuelles > 0 && (
                      <div className="flex justify-between text-sm" data-testid="charges-societe-mensuelles">
                        <span className="text-zinc-400">Frais fixes société (quote-part)
                          <span className="text-[10px] text-zinc-600 ml-1">part imputée</span>
                        </span>
                        <span className="font-mono text-purple-400">{formatCurrency(calculation.charges_societe_mensuelles)}/mois</span>
                      </div>
                    )}
                    {(calculation.total_mensuel_recurrent ?? calculation.total_mensuel_previsionnel ?? 0) > 0 && (
                      <div className="flex justify-between text-sm pt-2 border-t border-zinc-800" data-testid="total-mensuel-recurrent">
                        <span className="text-zinc-200 font-semibold">Frais mensuels cumulés</span>
                        <span className="font-mono text-red-400 font-bold">{formatCurrency(calculation.total_mensuel_recurrent ?? calculation.total_mensuel_previsionnel)}/mois</span>
                      </div>
                    )}

                    <div className="flex justify-between text-sm pt-2">
                      <span className="text-zinc-400">Seuil de rentabilité</span>
                      <span className="font-mono text-blue-400">{formatCurrency(calculation.seuil_rentabilite)}</span>
                    </div>
                    {calculation.rentabilite_m2 !== 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">Rentabilité nette / m²</span>
                        <span className={`font-mono ${calculation.rentabilite_m2 > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(calculation.rentabilite_m2)}/m²</span>
                      </div>
                    )}
                  </div>

                  <Button 
                    onClick={handleSave} 
                    className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="save-study-button"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer l'étude
                  </Button>
                  {isViable && (
                    <Button
                      onClick={handleLaunchProject}
                      className="w-full mt-2 bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white font-semibold shadow-lg shadow-emerald-500/20"
                      data-testid="launch-project-button"
                      title="Crée automatiquement un projet dans le pipeline avec tous les budgets pré-remplis"
                    >
                      <Rocket className="w-4 h-4 mr-2" />
                      🚀 Lancer ce projet
                    </Button>
                  )}
                  <Button
                    onClick={handleExportPdf}
                    variant="outline"
                    className="w-full mt-2 border-zinc-700 text-zinc-300"
                    data-testid="export-pdf-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exporter en PDF
                  </Button>
                </>
              ) : (
                <div className="text-center py-8">
                  <Calculator className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">
                    Sélectionnez un bien et remplissez les champs pour calculer la rentabilité
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
